// ── Risk Color System — Fraser Lever / Newmont RMS + Lake Vermont Priority ──
// Per-site color schemes allow each site to have independent colors.
// Currently Tanami and Lihir both use the NEM-RMS-STA-001-GDL-08 matrix colors:
//   Low (1-5) GREEN, Medium (6-10) BLUE, High (11-17) YELLOW, Extreme (18-25) RED
// Lake Vermont uses P1-P5 priority scale (separate system in priority-scale.ts).

import type { NodeType } from "../types/graph";
import { PRIORITY_HEX, PRIORITY_RGBA, normalizePriority } from "./priority-scale";

// ── Types ──

export type RiskLevel = "Low" | "Medium" | "High" | "Extreme" | "Unknown";
export type RMSRiskLevel = Exclude<RiskLevel, "Unknown">;

export interface SiteColorScheme {
  hex: Record<RiskLevel, string>;
  rgba: Record<RiskLevel, [number, number, number, number]>;
  /** Optional consequence vocabulary (e.g. Insignificant→Catastrophic) */
  consequenceHex?: Record<string, string>;
  consequenceRgba?: Record<string, [number, number, number, number]>;
}

// ── RMS Color Scheme (Newmont RMS matrix — NEM-RMS-STA-001-GDL-08) ──
// Low=GREEN, Medium=BLUE, High=YELLOW, Extreme=RED

export const RMS_RISK_LEVEL_HEX: Record<RiskLevel, string> = {
  Low: "#22c55e",      // green-500
  Medium: "#3b82f6",   // blue-500
  High: "#eab308",     // yellow-500
  Extreme: "#ef4444",  // red-500
  Unknown: "#6b7280",  // gray-500
};

export const RMS_RISK_LEVEL_RGBA: Record<RiskLevel, [number, number, number, number]> = {
  Low: [34, 197, 94, 200],
  Medium: [59, 130, 246, 200],
  High: [234, 179, 8, 200],
  Extreme: [239, 68, 68, 200],
  Unknown: [156, 163, 175, 160],
};

const RMS_SCHEME: SiteColorScheme = {
  hex: RMS_RISK_LEVEL_HEX,
  rgba: RMS_RISK_LEVEL_RGBA,
  consequenceHex: {
    insignificant: "#22c55e",
    minor: "#3b82f6",
    moderate: "#eab308",
    major: "#f97316",
    catastrophic: "#ef4444",
  },
  consequenceRgba: {
    insignificant: [34, 197, 94, 200],
    minor: [59, 130, 246, 200],
    moderate: [234, 179, 8, 200],
    major: [249, 115, 22, 200],
    catastrophic: [239, 68, 68, 200],
  },
};

// ── Site Color Registry ──
// Add new sites here when they need different colors.
// Both Tanami and Lihir use the same RMS scheme for now.

const SITE_SCHEMES: Record<string, SiteColorScheme> = {
  tanami: RMS_SCHEME,
  lihir: RMS_SCHEME,
  // "lake-vermont" uses priority-scale.ts (P1-P5), not risk levels
};

/** Default scheme used when no site is specified */
const DEFAULT_SCHEME = RMS_SCHEME;

/** Look up the color scheme for a site. Falls back to the default RMS scheme. */
export function getSchemeForSite(siteId: string | undefined): SiteColorScheme {
  if (!siteId) return DEFAULT_SCHEME;
  return SITE_SCHEMES[siteId] ?? DEFAULT_SCHEME;
}

// ── Legacy constants (for imports that reference these directly) ──

export const RISK_LEVEL_HEX = DEFAULT_SCHEME.hex;
export const RISK_LEVEL_RGBA = DEFAULT_SCHEME.rgba;

/**
 * Derive RMS risk level from a risk score (1-25).
 * Based on Newmont RMS matrix lookup table.
 */
export function riskLevelFromScore(score: number | undefined): RiskLevel {
  if (score === undefined || score <= 0) return "Unknown";
  if (score >= 18) return "Extreme";
  if (score >= 11) return "High";
  if (score >= 6) return "Medium";
  return "Low";
}

/**
 * Return hex color for a risk score (1-25) using RMS risk levels.
 * Low=GREEN, Medium=BLUE, High=YELLOW, Extreme=RED.
 */
export function severityColor(severity: number | undefined, siteId?: string): string {
  const scheme = getSchemeForSite(siteId);
  return scheme.hex[riskLevelFromScore(severity)];
}

/**
 * Return hex color for a named risk level string.
 * Handles RMS levels (Low/Medium/High/Extreme), consequence vocab, and LV priority (P1-P5).
 */
export function riskLevelColor(level: string, siteId?: string): string {
  const scheme = getSchemeForSite(siteId);

  // Try RMS level first
  if (level in scheme.hex) return scheme.hex[level as RiskLevel];

  // Try consequence vocabulary (Insignificant/Minor/Moderate/Major/Catastrophic)
  const conKey = level.trim().toLowerCase();
  if (scheme.consequenceHex && conKey in scheme.consequenceHex) return scheme.consequenceHex[conKey];

  // Try priority level (P1-P5)
  const normalized = normalizePriority(level);
  if (normalized !== "Unknown") return PRIORITY_HEX[normalized];

  return scheme.hex.Unknown;
}

/**
 * Return RGBA color for a risk score (for deck.gl ScatterplotLayer).
 */
export function riskScoreToRGBA(score: number | undefined, siteId?: string): [number, number, number, number] {
  const scheme = getSchemeForSite(siteId);
  return scheme.rgba[riskLevelFromScore(score)];
}

/**
 * Return RGBA color for a named risk level OR priority level string.
 * Use this in deck.gl layers when you have a string label instead of a numeric score.
 */
export function riskLevelToRGBA(level: string | undefined, siteId?: string): [number, number, number, number] {
  const scheme = getSchemeForSite(siteId);
  if (!level) return scheme.rgba.Unknown;
  // Try RMS level first
  if (level in scheme.rgba) return scheme.rgba[level as RiskLevel];
  // Try consequence vocabulary
  const conKey = level.trim().toLowerCase();
  if (scheme.consequenceRgba && conKey in scheme.consequenceRgba) return scheme.consequenceRgba[conKey];
  // Try priority level (P1-P5)
  const normalized = normalizePriority(level);
  if (normalized !== "Unknown") return PRIORITY_RGBA[normalized];
  return scheme.rgba.Unknown;
}

/** Fallback for nodes without severity */
const TYPE_COLORS: Record<NodeType, string> = {
  site: "#1f3a32",      // FL Evergreen
  campaign: "#2a5a4a",  // FL Forest
  area: "#7faa4e",      // Darker Leaf (contrast on white)
  element: "#557b6e",   // FL Forest-80
  inspection: "#798984", // FL Evergreen-60
};

export function nodeColor(
  type: NodeType,
  severity: number | undefined,
): string {
  if (type === "site") return TYPE_COLORS.site;
  if (severity !== undefined) return severityColor(severity);
  return TYPE_COLORS[type];
}

/** Glow/emissive intensity — higher risk scores glow brighter */
export function nodeEmissive(severity: number | undefined): number {
  if (severity === undefined) return 0.1;
  if (severity >= 18) return 0.9;
  if (severity >= 11) return 0.6;
  if (severity >= 6) return 0.3;
  return 0.1;
}

/** Risk level label from score — Newmont RMS vocabulary only */
export function severityLabel(severity: number): string {
  return riskLevelFromScore(severity);
}

/** Severity badge tailwind classes — based on RMS risk level */
export function severityBadgeClass(severity: number): string {
  const level = riskLevelFromScore(severity);
  switch (level) {
    case "Extreme": return "bg-red-600 text-white";
    case "High": return "bg-yellow-500 text-gray-900";
    case "Medium": return "bg-blue-500 text-white";
    case "Low": return "bg-green-500 text-white";
    default: return "bg-gray-400 text-white";
  }
}
